<template>
  <div
    class="sn-downpayment-percentage"
    :sn-input-props_classes="snInputProps_classes"
  >
    <sn-currency
      v-model="response"
      :style="{width: '70%'}"
      :error-messages="snInputProps_errorMessages"
      data-cy="down-payment-dollar-amount"
      @input="handleDownPaymentAmountInput($event);"
      @focus="$emit('set-percentage', percentage)"
    />
    <v-text-field
      :id="snInputProps_identifier"
      :value="percentage"
      :class="`percent-field ${snInputProps_errorMessages ? 'error--text' : ''}`"
      :placeholder="snInputProps_disabled ? null : snInputProps_placeholder"
      data-cy="downpayment-percentage"
      :required="snInputProps_required"
      :disabled="snInputProps_disabled"
      aria-label="downpayment percentage"
      :maxlength="snInputProps_maxLength <= 0 ? undefined : snInputProps_maxLength"
      max="100"
      min="0"
      type="number"
      inputmode="decimal"
      pattern="\d*"
      prepend-inner-icon="sn-icon-percent"
      autocomplete="nope"
      outlined
      @keypress="nonNegativePercent()"
      @blur="calculatePercentage();$emit('blur', $event)"
      @input="handlePercentageInput($event);"
      @focus="$emit('set-percentage', percentage); calculatePercentage()"
    />
  </div>
</template>

<script>
import SnInputProps from '../props/SnInputProps'
import snCurrency from '~/components/snui/v2/snCurrency/snCurrency'

export default {
  name: 'SnDownPayment',
  components: {
    snCurrency
  },
  props: {
    ...SnInputProps,
    price: {
      type: [Number, String],
      default: '0.00',
      required: false
    },
    value: {
      type: [String, Number, Array],
      default: '',
      required: false
    }
  },
  data () {
    return {
      response: '',
      percentage: ''
    }
  },
  watch: {
    value (newVal) {
      this.response = newVal
    },
    response (newVal) {
      this.$emit('input', this.response)
    }
  },
  created () {
    this.response = this.value
    if (this.value > 0) {
      this.calculatePercentage()
    }
  },
  methods: {
    handleDownPaymentAmountInput (value) {
      this.response = value
      this.calculatePercentage()
      this.$emit('focused-field', 'downpayment')
    },
    handlePercentageInput (value) {
      this.percentage = value
      this.$emit('set-percentage', this.percentage)
      this.calculateDownpayment()
      this.$emit('focused-field', 'percentage')
    },
    calculatePercentage () {
      if (this.response === 0) {
        this.percentage = '0'
        this.$emit('set-percentage', this.percentage)
      } else if (!this.response || !this.price) {
        this.percentage = ''
        return
      }

      const onlyNumericRegex = /[^\d.-]/g
      const purchasePrice = (typeof this.price) === 'string' ? parseFloat(this.price.replace(/[^\d.]/g, '')).toFixed(2) : parseFloat(this.price).toFixed(2)
      const downpayment = parseFloat(this.response.toString().replace(onlyNumericRegex, '')).toFixed(2)
      let percentString = parseFloat((downpayment / purchasePrice) * 100).toFixed(4)
      if (parseInt(percentString.split('.')[1]) === 0) {
        percentString = parseInt(percentString).toString()
      }
      this.percentage = parseFloat(percentString).toString()
      this.$emit('set-percentage', this.percentage)
    },
    nonNegativePercent (evt = window.event) {
      const expect = evt.target.value.toString() + evt.key.toString()

      if (expect.includes('-')) {
        evt.preventDefault()
      } else {
        return true
      }
    },
    calculateDownpayment () {
      if (!this.percentage || !this.price) {
        this.response = ''
        return
      }

      if (this.percentage > 100) this.percentage = 100
      const purchasePrice = (typeof this.price) === 'string' ? parseFloat(this.price.replace(/[^\d.]/g, '')).toFixed(2) : parseFloat(this.price).toFixed(2)
      const downpaymentPercentage = parseFloat(this.percentage).toFixed(4)
      const newVal = parseFloat(purchasePrice * (downpaymentPercentage / 100)).toFixed(2)
      const formattedVal = newVal.replace(/[^\d.]/g, '')
      this.response = parseFloat(formattedVal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~/assets/css/snui/index.scss';

.sn-downpayment-percentage {
  display: inline-flex;
  width: 100%;

  .v-input--is-disabled {
    ::v-deep .v-input__slot {
      background-color: $sn-gray-01;
      border: 1px solid $sn-gray-04;
      .v-input__prepend-inner {
        .v-icon {
          color: $sn-primary-01 !important;
        }
      }
      .v-text-field__slot {
        input {
          color: $sn-primary-01 !important;
        }
      }
    }
  }
  ::v-deep {
    font-family: $font-family-primary !important;

    > .sn-currency {
      width: 70%;

      .amount-field {
        .v-input__slot {
          border-bottom-right-radius: 0;
          border-top-right-radius: 0;
        }
      }
    }

    .percent-field {
      margin-left: -1px;

      .v-input__slot {
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
      }
    }

    > .sn-text {
      width: 30%;
      margin-left: -1px;

      .error--text {
        .v-messages {
          display: none;
        }
      }

      .v-input--is-disabled {
        .v-input__slot {
          border-left: 1px solid $sn-gray-04;
        }
      }
    }

    .v-input__slot {
      border: 1px solid $sn-gray-04;
      box-sizing: border-box;
      border-radius: 2px;
      min-height: 48px !important;

      & fieldset {
        border: none;
      }

      &:focus-within {
        border: 1px solid $sn-primary !important;
      }

      &:hover {
        border: 1px solid $sn-primary !important;
        z-index: 99;
      }

      .v-input__prepend-inner {
        margin-top: 10px;

        .v-icon {
          font-family: 'open sans' !important;
          font-size: 16px;
          color: $sn-primary !important;
        }
      }
    }

    .v-messages {
      color: $sn-primary-01;
      visibility: hidden;

      &.error--text {
        font-size: 15px;
        display: inline-flex;
        align-items: center;
        visibility: visible;

        .v-messages__wrapper {
          display: inline-flex;
          padding-bottom: 2px;
        }
      }
    }

    .v-text-field__slot {
      input {
        caret-color: $sn-primary;
      }
    }

    .v-text-field__details {
      padding-left: 0 !important;
    }
  }
  .error--text {
    ::v-deep .v-input__slot {
      border-color: #FF5252;

      &:hover {
        border-color: #FF5252 !important;
      }
    }
  }
}
</style>
